import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

const Home = () => {
    useEffect(() => {
        const sections = document.querySelectorAll('.fade-in-section');

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                }
            });
        }, { threshold: 0.15 }); // Trigger when 15% of the element is visible

        sections.forEach(section => {
            observer.observe(section);
        });

        // Clean up observer on component unmount
        return () => {
            sections.forEach(section => {
                observer.unobserve(section);
            });
        };
    }, []);

    return (
        <div className="home-container">
            {/* Hero Section */}
            <div className="logo-container">
                <img src="logo.png" alt="Dog Logo" className="logo" />
                <h1 className="site-title">Daily Dog</h1>
            </div>

            <section className="hero-section">
                <div className="hero-content">
                    <div className="hero-text">
                        <h1>Daily New Activities for You and Your Dog</h1>
                        <p className="hero-tagline">Creative ideas to strengthen your bond through shared experiences</p>
                        <div className="store-buttons-container">
                            <a
                                target="_blank"
                                href="https://apps.apple.com/de/app/daily-dog-t%C3%A4gliche-%C3%BCbungen/id6741107290"
                                className="store-button app-store"
                            >
                                <span className="button-icon">
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                    </svg>
                                </span>
                                <span className="button-text">App Store</span>
                            </a>
                            <a
                                href="#"
                                className="store-button google-play"
                            >
                                <span className="button-icon">
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                                        <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
                                    </svg>
                                </span>
                                <span className="button-text">Google Play</span>
                            </a>
                        </div>
                    </div>
                    <div className="hero-image">
                        <img
                            src="/Landingpage.png"
                            alt="Daily Dog App Preview"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/300x600?text=App+Preview";
                            }}
                        />
                    </div>
                </div>
                <div className="wave-divider"></div>
            </section>

            {/* Promotion Section */}
            <section className="promo-section fade-in-section">
                <div className="promo-content">
                    <h2>Never Run Out of Ideas for Your Dog</h2>
                    <p>
                        Daily Dog delivers fresh, engaging activities straight to your phone every day.
                        Keep your furry friend mentally stimulated, physically active, and always excited for
                        what's next. From quick indoor games to outdoor adventures, we've got your daily
                        dose of canine inspiration covered.
                    </p>
                </div>
            </section>

            {/* Screenshot Showcase */}
            <section className="screenshots-section fade-in-section">
                <div className="section-header">
                    <h2>See Daily Dog in Action</h2>
                    <p>Explore the intuitive interface and discover engaging activities for every day</p>
                </div>
                <div className="screenshots-container">
                    <div className="screenshot">
                        <img
                            src="/Screenshot1.png"
                            alt="Daily Dog Screenshot 1"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/250x500?text=Screenshot+1";
                            }}
                        />
                    </div>
                    <div className="screenshot">
                        <img
                            src="/Screenshot2.png"
                            alt="Daily Dog Screenshot 2"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/250x500?text=Screenshot+2";
                            }}
                        />
                    </div>
                    <div className="screenshot">
                        <img
                            src="/Screenshot3.png"
                            alt="Daily Dog Screenshot 3"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/250x500?text=Screenshot+3";
                            }}
                        />
                    </div>
                    <div className="screenshot">
                        <img
                            src="/Screenshot4.png"
                            alt="Daily Dog Screenshot 4"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/250x500?text=Screenshot+4";
                            }}
                        />
                    </div>
                </div>
            </section>

            {/* Customer Reviews */}
            <section className="reviews-section fade-in-section">
                <div className="section-header">
                    <h2>What Dog Owners Say</h2>
                    <p>Join our community and discover the joy of pet parenthood with Daily Dog</p>
                </div>
                <div className="reviews-container">
                    <div className="review-card fade-in-section">
                        <div className="review-stars">
                            ★★★★★
                        </div>
                        <p className="review-text">
                            "This app has been a game-changer for me and my dog. We now have something new to try every day and she's so much calmer as a result!"
                        </p>
                        <div className="reviewer">
                            <div className="reviewer-avatar">
                                <span>UM</span>
                            </div>
                            <div className="reviewer-info">
                                <h4>Ulrike M.</h4>
                                <p>Poodle owner</p>
                            </div>
                        </div>
                    </div>

                    <div className="review-card fade-in-section">
                        <div className="review-stars">
                            ★★★★★
                        </div>
                        <p className="review-text">
                            "Daily Dog has improved our daily time together with innovative and engaging challenges that bring out the best in her playful personality!"
                        </p>
                        <div className="reviewer">
                            <div className="reviewer-avatar">
                                <span>HH</span>
                            </div>
                            <div className="reviewer-info">
                                <h4>Heidi H.</h4>
                                <p>Schnauzer Mix owner</p>
                            </div>
                        </div>
                    </div>
                    <div className="review-card fade-in-section">
                        <div className="review-stars">
                            ★★★★★
                        </div>
                        <p className="review-text">
                            "As a first-time dog parent, I had no idea how to keep my puppy entertained. The daily challenges are perfect and he's learning so quickly!"
                        </p>
                        <div className="reviewer">
                            <div className="reviewer-avatar">
                                <span>MD</span>
                            </div>
                            <div className="reviewer-info">
                                <h4>Mario D.</h4>
                                <p>Labrador owner</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="site-footer">
                <div className="footer-content">
                    <div className="footer-logo">
                        <img
                            src="/logo.png"
                            alt="Daily Dog Logo"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/40x40?text=DD";
                            }}
                        />
                        <h3 style={{ fontFamily: "Baloo, sans-serif" }}>Daily Dog</h3>
                    </div>
                    <div className="footer-links">
                        <div className="footer-links-column">
                            <h4>Legal</h4>
                            <ul>
                                <li><Link to="/terms">Terms of Service</Link></li>
                                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                            </ul>
                        </div>
                        <div className="footer-links-column">
                            <h4>Help</h4>
                            <ul>
                                <li><Link to="/support">Support</Link></li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>&copy; {new Date().getFullYear()} Daily Dog. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default Home;